import { useMemo, Dispatch, SetStateAction } from 'react';
import { OrganizationFileResponse } from '@server/application/files/types/file.types';
import { ColumnDef, Row } from '@tanstack/react-table';
import { Pagination } from 'antd';
import { AttachedFileDto } from '@/shared/api';
import { PagingData, useModifiedListSelection } from '@/shared/hooks';
import { FileExplorerTable } from '@/shared/ui';
import { FileExplorerActionsColumn } from '@/widgets/knowledge-base/FileExplorerActionsColumn';
import { FileExplorerLabelColumn } from '@/widgets/knowledge-base/FileExplorerLabelColumn';
import { KnowledgeBaseFileExplorerSearch } from '@/widgets/knowledge-base/KnowledgeBaseFileExplorerSearch';
import { FileExplorerSourceColumn } from './FileExplorerSourceColumn';
import {
  FileExplorerStatusColumn,
  FileProcessingStatus,
  getFileProgress,
} from './FileExplorerStatusColumn';
import { KnowledgeBaseFileExplorerContext } from './KnowledgeBaseFileExplorerContext';
import { useManageTagsData } from './useManageTagsData';

interface KnowledgeBaseFileExplorerProps extends PagingData {
  files?: OrganizationFileResponse[];
  total?: number;
  onFilesSelect?: (files: AttachedFileDto[]) => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isPolling?: boolean;
  searchTerm: string;
  onSearchTermChange: Dispatch<SetStateAction<string>>;
  selectedTags: string[];
  onSelectedTagsChange: Dispatch<SetStateAction<string[]>>;
}

const toAttachedFileDto = ({
  id,
  fileName,
  organizationId,
}: OrganizationFileResponse) => ({ fileId: id, fileName, organizationId });

const enableRowSelection = ({ original }: Row<OrganizationFileResponse>) =>
  getFileProgress((original as OrganizationFileResponse).status).status ===
  FileProcessingStatus.Success;

const getColumns = (
  isReadOnly?: boolean,
): ColumnDef<OrganizationFileResponse>[] => {
  const columns: ColumnDef<OrganizationFileResponse>[] = [
    {
      id: 'status',
      header: 'Status',
      meta: { align: 'center' },
      size: 100,
      cell: FileExplorerStatusColumn,
    },
    {
      id: 'source',
      header: 'Source',
      meta: { align: 'center' },
      size: 150,
      cell: FileExplorerSourceColumn,
    },
    {
      id: 'label',
      header: 'Label',
      size: 150,
      cell: FileExplorerLabelColumn,
    },
  ];
  if (!isReadOnly) {
    columns.push({
      id: 'actions',
      header: 'Actions',
      meta: { align: 'center' },
      size: 100,
      cell: FileExplorerActionsColumn,
    });
  }
  return columns;
};

export const KnowledgeBaseFileExplorer = ({
  files,
  total,
  isLoading,
  isReadOnly,
  isPolling,
  onFilesSelect,
  page,
  pageSize,
  pageSizeOptions,
  onPagingChange,
  searchTerm,
  onSearchTermChange,
  selectedTags,
  onSelectedTagsChange,
}: KnowledgeBaseFileExplorerProps) => {
  const {
    labels,
    onLabelCreate,
    onLabelSelect,
    onLabelDeselect,
    onLabelDelete,
  } = useManageTagsData();
  const columns = useMemo(() => getColumns(isReadOnly), [isReadOnly]);
  const [rowSelection, setRowSelection] = useModifiedListSelection(
    files,
    onFilesSelect,
    toAttachedFileDto,
  );

  const value = useMemo(
    () => ({
      isPolling,
      labels,
      onLabelCreate,
      onLabelSelect,
      onLabelDeselect,
      onLabelDelete,
    }),
    [
      isPolling,
      labels,
      onLabelCreate,
      onLabelSelect,
      onLabelDeselect,
      onLabelDelete,
    ],
  );

  return (
    <KnowledgeBaseFileExplorerContext.Provider value={value}>
      <KnowledgeBaseFileExplorerSearch
        labels={labels}
        onLabelCreate={onLabelCreate}
        onLabelDelete={onLabelDelete}
        searchTerm={searchTerm}
        onSearchTermChange={onSearchTermChange}
        selectedTags={selectedTags}
        onSelectedTagsChange={onSelectedTagsChange}
      />
      <FileExplorerTable
        columns={columns}
        data={files}
        rowSelection={rowSelection}
        onRowSelect={setRowSelection}
        rowNameKey="fileName"
        enableRowSelection={enableRowSelection}
        showRowsSelection={isReadOnly}
        loading={isLoading}
        autoResetRowsSelection={false}
      />
      <Pagination
        hideOnSinglePage
        showSizeChanger
        disabled={isLoading}
        total={total}
        current={page}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onChange={onPagingChange}
      />
    </KnowledgeBaseFileExplorerContext.Provider>
  );
};
